import { FC, Key, ReactNode, useMemo, useState } from 'react';
import { Alert, Button, Empty, Input, Segmented, Select, Table, Tabs } from 'antd';
import { BarChartOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { NAMESPACE, DATE_FORMAT, PRIVATE_PATH } from '@/constants';
import { formatMoney, formatCompactMoney } from '@/constants/currency';
import { pipedriveDealUrl } from '@/utils';
import { useGetBookingFilterOptionsQuery } from '@/api';
import { NoData, Content, Loader } from '@/components';
import type { BookingRow, CampaignGroupFields } from '@/models';
import { CampaignTimeline } from './components/CampaignTimeline';
import { EditBookingModal } from './components/EditBookingModal';
import { useCampaignGroups } from './hooks/useCampaignGroups';
import type { CampaignGroup } from './hooks/useCampaignGroups';
import { useBookingSheet } from './hooks/useBookingSheet';
import { toCountryOptions, toOptions } from './utils/filterOptions';
import { summaryMoney } from './utils/summaryMoney';
import { COLUMN_DEFS, COLUMN_ORDER, DEFAULT_VISIBLE_COLUMNS } from './BookingSheet.constants';
import type {
  BookingColumnKey,
  FlightStatus,
  TimelineSort,
  TimelineZoom
} from './BookingSheet.types';
import {
  currentQuarterKey,
  quartersIn
} from './components/CampaignTimeline/useTimelineModel';
import { ColumnToggle } from './components/ColumnToggle/ColumnToggle';
import { CampaignLineItems } from './components/CampaignLineItems';
import {
  FilterField,
  FilterHeader,
  FilterHeaderSearch,
  FilterRow,
  FilterLabel,
  StyledContainer,
  SummaryGrid,
  SummaryCard,
  SummaryLabel,
  FilterBarHeader,
  FilterBarTitle,
  SummaryValue,
  SummarySkeleton,
  TableScrollContainer,
  MixedValue,
  ViewField,
  ViewGroup,
  ViewLabel,
  ViewRow,
  ViewSpacer
,
  AwaitingNote
} from './BookingSheet.styles';

const { Search } = Input;

const MONEY_FIELDS: BookingColumnKey[] = ['rate', 'gross', 'media_spend', 'fee', 'discount', 'net'];
const DATE_FIELDS: BookingColumnKey[] = ['won_date', 'start_date', 'end_date'];
const COUNT_FIELDS: BookingColumnKey[] = ['quantity'];

/** Shown wherever a cell has no value, so an empty column reads as deliberate
 *  rather than as something that failed to load. */
const EMPTY = '—';

/** What still names a campaign once it is open; its other cells are left to
 *  the lines beneath it. */
const CAMPAIGN_IDENTITY_KEYS = new Set(['advertiser', 'campaign', 'deal_id']);

const formatDate = (value?: string): string => {
  if (!value) return EMPTY;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(DATE_FORMAT) : EMPTY;
};

/** Impressions and similar counts: grouped, never with a currency symbol. */
const formatCount = (value?: number): string =>
  typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : EMPTY;

const formatCell = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return EMPTY;
  return String(value);
};

/** Unique, sorted option list for a filter, derived from whatever rows are currently loaded. */
/**
 * Renders a summary figure, or a dash when it is missing.
 *
 * The guard is deliberate: a field the API stops sending should leave one card
 * blank, not take the whole page down with a format call on undefined.
 *
 * Counts and money are formatted differently on purpose. A count is exact and
 * small enough to read whole; a total across the entire booking database runs
 * to eight digits, where raw float pennies (£51,928,510.264) are noise rather
 * than precision — so money is shortened to its magnitude.
 */
const summaryValue = (value: number | undefined, format: (n: number) => string): string =>
  typeof value === 'number' && Number.isFinite(value) ? format(value) : '—';

const BookingSheet: FC = () => {
  const [editing, setEditing] = useState<BookingRow | null>(null);
  const { t } = useTranslation(NAMESPACE.BOOKING_SHEET_PAGE);
  const {
    rows,
    total,
    summary,
    awaiting,
    isLoading,
    isFetching,
    isError,
    isSummaryError,
    isSummaryFetching,
    timeline,
    isTimelineLoading,
    isTimelineError,
    page,
    limit,
    handlePaginationChange,
    search,
    searchInput,
    handleSearch,
    filterValues,
    setFilterValues,
    view,
    setView,
    isViewDefault,
    resetAllFilters,
    hasActiveFilterValues,
    isFiltered,
    handleSort
  } = useBookingSheet();

  const [visibleColumns, setVisibleColumns] = useState<Set<BookingColumnKey>>(
    () => new Set(DEFAULT_VISIBLE_COLUMNS)
  );
  const [activeTab, setActiveTab] = useState('bookings');
  /* Open by default: the overview is what the timeline tab is first read for,
     and the toggle is there to reclaim the space when the bars are the point. */
  const [showCharts, setShowCharts] = useState(true);
  /* Owned here rather than in the hook: it is set by clicking the timeline's
     mix chart, and means nothing to the table. */
  const [timelineChannel, setTimelineChannel] = useState<string | null>(null);

  const isTimelineTab = activeTab === 'timeline';

  const quarterOptions = useMemo(() => {
    const keys = quartersIn(timeline ?? []);
    const current = currentQuarterKey();
    // Always offered, even in a dataset that does not reach it, so the default
    // selection is never an option that is not there.
    if (!keys.includes(current)) keys.push(current);
    return [
      { value: '__all', label: t('timelineAllQuarters') },
      ...keys
        .sort()
        .reverse()
        .map((key) => ({ value: key, label: key.replace('-', ' ') }))
    ];
  }, [timeline, t]);

  /* From every live booking, not the page on screen: built from the rows, the
     dropdowns offered only what one page of campaigns held, and a campaign
     whose lines differ carries no single channel or country at all -- so Audio
     or Germany could be missing altogether. */
  const { data: filterOptions } = useGetBookingFilterOptionsQuery();
  const channelOptions = useMemo(() => toOptions(filterOptions?.channel), [filterOptions]);
  const platformOptions = useMemo(() => toOptions(filterOptions?.platform), [filterOptions]);
  const countryOptions = useMemo(() => toCountryOptions(filterOptions?.country), [filterOptions]);
  const buyModelOptions = useMemo(() => toOptions(filterOptions?.buyModel), [filterOptions]);

  const columns: ColumnsType<BookingRow> = useMemo(
    () =>
      COLUMN_ORDER.filter((key) => visibleColumns.has(key)).map((key) => {
        const def = COLUMN_DEFS[key];
        const base: ColumnsType<BookingRow>[number] = {
          ...def,
          title: t(def.i18nKey)
        };

        /* The deal id opens its PipeDrive deal. Opened in a new tab: the
           booking sheet is read while cross-checking a deal, and replacing it
           would lose the filters and scroll position that got here. */
        if (key === 'deal_id') {
          return {
            ...base,
            render: (value: unknown) => {
              const url = pipedriveDealUrl(value as number | string | null);
              if (!url) return formatCell(value);
              return (
                <a href={url} target="_blank" rel="noreferrer noopener">
                  {formatCell(value)}
                </a>
              );
            }
          };
        }

        /* The campaign opens the media plan it was booked from, so a line can
           be traced back without searching for its deal. A new tab, for the
           same reason as the deal id: the filters that found it are kept. */
        if (key === 'campaign') {
          return {
            ...base,
            render: (value: unknown, row: BookingRow) =>
              row.nrve_media_plan_id ? (
                <Link
                  to={`${PRIVATE_PATH.MEDIA_PLAN}/${row.nrve_media_plan_id}`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {formatCell(value)}
                </Link>
              ) : (
                formatCell(value)
              )
          };
        }

        if ((DATE_FIELDS as string[]).includes(key)) {
          return { ...base, render: (value: string) => formatDate(value) };
        }

        if ((MONEY_FIELDS as string[]).includes(key)) {
          return {
            ...base,
            render: (value: number, row: BookingRow) =>
              typeof value === 'number' && Number.isFinite(value)
                ? formatMoney(value, row.currency)
                : EMPTY
          };
        }

        if ((COUNT_FIELDS as string[]).includes(key)) {
          return { ...base, render: (value: number) => formatCount(value) };
        }

        return { ...base, render: (value: unknown) => formatCell(value) };
      }),
    [visibleColumns, t]
  );

  const campaignGroups = useCampaignGroups(rows);

  /**
   * Campaign columns, plus an edit action for a campaign of one line.
   *
   * Such a campaign cannot be expanded — there is nothing to reveal — so
   * without this its only booking had no way to be corrected. The group row
   * spreads its single line item, so it carries that line's own id and is
   * addressable. A campaign of several lines keeps the action on the lines
   * themselves, since a correction applies to one of them rather than to the
   * grouping.
   */
  const [expandedKeys, setExpandedKeys] = useState<readonly Key[]>([]);

  const campaignColumns = useMemo(
    () => [
      ...(columns as ColumnsType<CampaignGroup>).map((column) => {
        const key = String(column.key);
        const render = (column as { render?: (value: unknown, row: CampaignGroup, index: number) => ReactNode })
          .render;

        return {
          ...column,
          render: (value: unknown, group: CampaignGroup, index: number) => {
            /* An open campaign is shown by its lines beneath it, so it keeps
               only what names it. Repeating its totals and spans above lines
               that carry their own read as two layers of the same figures. */
            if (expandedKeys.includes(group.groupKey) && !CAMPAIGN_IDENTITY_KEYS.has(key)) return null;

            // The lines disagree, so there is no one value to show.
            if ((group as CampaignGroup & CampaignGroupFields).mixed_fields?.includes(key)) {
              return <MixedValue>{t('mixedValue')}</MixedValue>;
            }

            return render ? render(value, group, index) : (value as ReactNode);
          }
        };
      }),
      {
        title: '',
        key: 'actions',
        width: 72,
        fixed: 'right' as const,
        render: (_: unknown, group: CampaignGroup) =>
          group.lineItemCount === 1 ? (
            <Button type="link" size="small" onClick={() => setEditing(group)}>
              {t('edit', { ns: NAMESPACE.COMMON })}
            </Button>
          ) : null
      }
    ],
    [columns, expandedKeys, t]
  );

  // The nested table reuses the parent's columns so a line item is read under
  // the same headings, minus the expand affordance.
  const lineItemColumns = useMemo(
    () => [
      ...columns.map((column) => ({ ...column, sorter: undefined })),
      {
        /* Only on the nested rows: a line item is the thing a correction
           applies to. The campaign row above it is a grouping, not a booking,
           and has no id to address. */
        title: '',
        key: 'actions',
        width: 72,
        fixed: 'right' as const,
        render: (_: unknown, record: BookingRow) => (
          <Button type="link" size="small" onClick={() => setEditing(record)}>
            {t('edit', { ns: NAMESPACE.COMMON })}
          </Button>
        )
      }
    ],
    [columns, t]
  );

  if (isLoading) {
    return (
      <>
        <Content>
          <Loader fullscreen={false} />
        </Content>
      </>
    );
  }

  // "No bookings yet" only applies when nothing has ever been booked AND the user
  // hasn't applied any filter/search — otherwise it's "no rows match your filters".
  if (!isFetching && total === 0 && !isFiltered) {
    return (
      <>
        <Content>
          <NoData title={t('noBookingsTitle')} subtitle={t('noBookingsSubtitle')} />
        </Content>
      </>
    );
  }

  /* The filter bar lives in the page header rather than above the content:
     it governs both tabs, so it belongs with the page's identity, and moving
     it there buys back a full block of vertical space before the data. */
  /**
   * The page's single filter bar, living in the header.
   *
   * Two rows, split by what the controls do rather than where they came from:
   * the first narrows *which bookings* (and so drives both tabs, all of it
   * server-side), the second shapes *how the timeline draws them*. Scale and
   * sort have no meaning for a table, so on that tab they dim in place
   * instead of disappearing and changing the bar's shape.
   */
  const filterBar = (
    <FilterHeader data-fill>
      <FilterRow>
        <FilterHeaderSearch>
          <FilterLabel htmlFor="booking-filter-search">{t('searchFieldLabel')}</FilterLabel>
          {/* Controlled, not defaultValue: an uncontrolled input kept its
              text after Reset cleared the filter, leaving the box showing a
              term that was no longer being applied. */}
          <Search
            id="booking-filter-search"
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t('searchPlaceholder')}
            value={searchInput}
            aria-label={t('searchLabel')}
            allowClear
          />
        </FilterHeaderSearch>

        <FilterField>
          <FilterLabel htmlFor="booking-filter-channel">{t('channelFilterLabel')}</FilterLabel>
          <Select
            id="booking-filter-channel"
            allowClear
            showSearch
            placeholder={t('anyOption')}
            options={channelOptions}
            value={filterValues?.channel}
            optionFilterProp="label"
            onChange={(v) => setFilterValues({ channel: v })}
            style={{ width: '100%' }}
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
          />
        </FilterField>

        <FilterField>
          <FilterLabel htmlFor="booking-filter-platform">{t('platformFilterLabel')}</FilterLabel>
          <Select
            id="booking-filter-platform"
            allowClear
            showSearch
            placeholder={t('anyOption')}
            options={platformOptions}
            value={filterValues?.platform}
            optionFilterProp="label"
            onChange={(v) => setFilterValues({ platform: v })}
            style={{ width: '100%' }}
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
          />
        </FilterField>

        <FilterField>
          <FilterLabel htmlFor="booking-filter-country">{t('countryFilterLabel')}</FilterLabel>
          <Select
            id="booking-filter-country"
            allowClear
            showSearch
            placeholder={t('anyOption')}
            options={countryOptions}
            value={filterValues?.country}
            optionFilterProp="label"
            onChange={(v) => setFilterValues({ country: v })}
            style={{ width: '100%' }}
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
          />
        </FilterField>

        <FilterField>
          <FilterLabel htmlFor="booking-filter-buy-model">{t('buyModelFilterLabel')}</FilterLabel>
          <Select
            id="booking-filter-buy-model"
            allowClear
            showSearch
            placeholder={t('anyOption')}
            options={buyModelOptions}
            value={filterValues?.buyModel}
            optionFilterProp="label"
            onChange={(v) => setFilterValues({ buyModel: v })}
            style={{ width: '100%' }}
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
          />
        </FilterField>

        <FilterField>
          <FilterLabel htmlFor="booking-filter-quarter">{t('timelineQuarter')}</FilterLabel>
          <Select
            id="booking-filter-quarter"
            value={view.quarter ?? '__all'}
            onChange={(value) => setView({ quarter: value === '__all' ? null : value })}
            options={quarterOptions}
            style={{ width: '100%' }}
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
          />
        </FilterField>
      </FilterRow>

      <ViewRow>
        <ViewField>
          <ViewLabel as="span">{t('timelineStatus')}</ViewLabel>
          <Segmented
            size="small"
            value={view.status}
            onChange={(value) => setView({ status: value as FlightStatus })}
            options={[
              { label: t('timelineStatusAll'), value: 'all' },
              { label: t('timelineStatusLive'), value: 'live' },
              { label: t('timelineStatusUpcoming'), value: 'upcoming' },
              { label: t('timelineStatusEnded'), value: 'ended' }
            ]}
          />
        </ViewField>

        <ViewGroup $inactive={!isTimelineTab}>
          <ViewField>
            <ViewLabel as="span">{t('timelineZoom')}</ViewLabel>
            <Segmented
              size="small"
              disabled={!isTimelineTab}
              value={view.zoom}
              onChange={(value) => setView({ zoom: value as TimelineZoom })}
              options={[
                { label: t('timelineZoomQuarter'), value: 'quarter' },
                { label: t('timelineZoomMonth'), value: 'month' },
                { label: t('timelineZoomWeek'), value: 'week' }
              ]}
            />
          </ViewField>

          <ViewField>
            <ViewLabel as="span">{t('timelineSort')}</ViewLabel>
            <Select
              size="small"
              disabled={!isTimelineTab}
              value={view.sort}
              onChange={(value) => setView({ sort: value as TimelineSort })}
              style={{ width: 142 }}
              aria-label={t('timelineSort')}
              getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
              options={[
                { label: t('timelineSortStart'), value: 'start' },
                { label: t('timelineSortNet'), value: 'net' },
                { label: t('timelineSortDuration'), value: 'duration' },
                { label: t('timelineSortCampaign'), value: 'campaign' }
              ]}
            />
          </ViewField>
        </ViewGroup>

        <ViewSpacer />

        <Button
          size="small"
          type={showCharts ? 'primary' : 'default'}
          ghost={showCharts}
          disabled={!isTimelineTab}
          icon={<BarChartOutlined />}
          aria-pressed={showCharts}
          onClick={() => setShowCharts((open) => !open)}
        >
          {t('timelineCharts')}
        </Button>
        <ColumnToggle visibleColumns={visibleColumns} onChange={setVisibleColumns} />
        <Button
          size="small"
          onClick={resetAllFilters}
          disabled={!hasActiveFilterValues && !search && isViewDefault}
        >
          {t('resetFilters')}
        </Button>
      </ViewRow>
    </FilterHeader>
  );

  return (
    <>
      <FilterBarHeader>
        <FilterBarTitle>{t('title')}</FilterBarTitle>
        {filterBar}
      </FilterBarHeader>
      <Content>
        <StyledContainer>
          {isError && <Alert type="error" role="alert" showIcon message={t('loadFailed')} />}
          {isSummaryError && <Alert type="error" role="alert" showIcon message={t('summaryLoadFailed')} />}

          {/* Held bookings are absent from everything else on this page by
              design, so without this the only signal is a campaign quietly
              having fewer line items than it should. The value is named too:
              how much is waiting matters more than how many rows. */}
          {/* One line rather than a warning banner. It reports a handful of
              line items against a page totalling tens of millions, so it
              should be noticeable without claiming the top of the screen —
              the detail of why belongs on the IO, where the action is. */}
          {Boolean(awaiting?.lineItems) && (
            <AwaitingNote title={t('awaitingClientOrderHint')}>
              <InfoCircleOutlined aria-hidden />
              <span>
                {t('awaitingClientOrderNote', {
                  lineItems: awaiting?.lineItems ?? 0,
                  campaigns: awaiting?.campaigns.length ?? 0,
                  gross: formatCompactMoney(awaiting?.gross)
                })}
              </span>
            </AwaitingNote>
          )}

          <SummaryGrid>
            <SummaryCard>
              <SummaryLabel>{t('summaryBookings')}</SummaryLabel>
              {isSummaryFetching ? (
                <SummarySkeleton />
              ) : (
                <SummaryValue>{summaryValue(summary?.rowCount, formatCount)}</SummaryValue>
              )}
            </SummaryCard>
            <SummaryCard>
              <SummaryLabel>{t('summaryDeals')}</SummaryLabel>
              {isSummaryFetching ? (
                <SummarySkeleton />
              ) : (
                <SummaryValue>{summaryValue(summary?.distinctDeals, formatCount)}</SummaryValue>
              )}
            </SummaryCard>
            <SummaryCard $money>
              <SummaryLabel>{t('summaryGross')}</SummaryLabel>
              {isSummaryFetching ? (
                <SummarySkeleton />
              ) : (
                <SummaryValue>{summaryMoney(summary, 'gross')}</SummaryValue>
              )}
            </SummaryCard>
            <SummaryCard $money>
              <SummaryLabel>{t('summaryNet')}</SummaryLabel>
              {isSummaryFetching ? (
                <SummarySkeleton />
              ) : (
                <SummaryValue>{summaryMoney(summary, 'net')}</SummaryValue>
              )}
            </SummaryCard>
          </SummaryGrid>


          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'bookings',
                label: t('tabBookings'),
                children: (
                  <TableScrollContainer>
                    <Table<CampaignGroup>
              rowKey="groupKey"
              columns={campaignColumns}
              dataSource={campaignGroups}
              expandable={{
                expandedRowKeys: expandedKeys,
                onExpandedRowsChange: setExpandedKeys,
                // A single-line campaign has nothing to reveal.
                rowExpandable: (group) => group.lineItemCount > 1,
                /* Fetched on expand rather than read from the row: the list
                   endpoint sends campaigns already collapsed, which is what
                   makes its page count describe the rows on screen. */
                expandedRowRender: (group) => (
                  <CampaignLineItems
                    advertiser={group.advertiser ?? ''}
                    campaign={group.campaign ?? ''}
                    columns={lineItemColumns}
                    lineItemCount={group.lineItemCount}
                  />
                )
              }}
              loading={isFetching}
              onChange={(_, __, sorter, { action }) => {
                /* antd fires this on every page change too, right after the
                   pagination callback — and handleSort resets to page 1, so
                   reacting to anything but a sort snapped page 2 back to 1. */
                if (action !== 'sort') return;
                // CampaignGroup extends BookingRow, so the sorted column key is
                // the same set either way.
                if (!Array.isArray(sorter)) handleSort(sorter as unknown as SorterResult<BookingRow>);
              }}
              scroll={{ x: 'max-content' }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={isFiltered ? t('noResultsTitle') : t('noBookingsTitle')}
                  />
                )
              }}
              pagination={{
                disabled: isFetching,
                showQuickJumper: true,
                // The count is campaigns now, matching the rows on screen --
                // it used to be line items, which offered pages that did not
                // exist once those lines were grouped.
                showTotal: (totalCount) => t('totalCampaigns', { total: totalCount }),
                defaultPageSize: limit,
                pageSize: limit,
                total,
                onChange: handlePaginationChange,
                current: page
              }}
                    />
                  </TableScrollContainer>
                )
              },
              {
                key: 'timeline',
                label: t('tabTimeline'),
                children: (
                  <CampaignTimeline
                    rows={timeline ?? []}
                    isLoading={isTimelineLoading}
                    isError={isTimelineError}
                    view={view}
                    channel={timelineChannel}
                    onChannelSelect={setTimelineChannel}
                    showCharts={showCharts}
                    onResetFilters={resetAllFilters}
                  />
                )
              }
            ]}
          />
        </StyledContainer>
      </Content>
      <EditBookingModal booking={editing} onClose={() => setEditing(null)} />
    </>
  );
};

export default BookingSheet;
